import type { Logger } from "@expert/logging";

import { isAudioQuickConnectName } from "@expert/shared-types";

import type { Contact } from "@/shared-types";
import type { Partner } from "@/shared-types/partner";

import { useContactsStateStore } from "@/call-controls/TransferCall/vdnContacts.store";

import withCallbacks from "../withCallbacks";

export interface EndpointMaps {
    queueEndpoints: Map<string, connect.Endpoint>;
}

export const fetchEndpointMaps = async (
    agent: connect.Agent,
    partner: Partner,
    logger: Logger,
    isTransferListEnabled: boolean,
): Promise<EndpointMaps> => {
    // If the transfer list is disabled, for example for clients who
    // disable outbound calls, then return empty maps.
    if (!isTransferListEnabled) {
        return {
            queueEndpoints: new Map<string, connect.Endpoint>(),
        };
    }

    const { endpoints } = await withCallbacks<connect.GetEndpointsResult>((callbacks) =>
        agent.getEndpoints(agent.getAllQueueARNs(), callbacks),
    );

    const queueEndpoints = new Map<string, connect.Endpoint>();

    for (const endpoint of endpoints) {
        // Audio-disclosure Quick Connects are dialed by ARN directly (see getDisclosureEndpointArn),
        // not transferred to — keep them out of the agent's transfer list / contacts.
        if (endpoint.name && isAudioQuickConnectName(endpoint.name)) continue;

        if (endpoint.type === connect.EndpointType.PHONE_NUMBER) {
            useContactsStateStore.getState().addContact(mapPhoneNumberEndpointToContact(endpoint, partner));
        } else if (endpoint.type === connect.EndpointType.QUEUE) {
            queueEndpoints.set(endpoint.endpointARN, endpoint);
            useContactsStateStore.getState().addContact(mapQueueEndpointToContact(endpoint, partner));
        } else {
            logger.warn(`Endpoint type not supported: '${endpoint.name}' ${endpoint.type}.`);
        }
    }

    return { queueEndpoints };
};

function mapPhoneNumberEndpointToContact(endpoint: connect.Endpoint, partner: Partner): Contact {
    return {
        name: endpoint.name,
        number: endpoint.phoneNumber,
        vdn: endpoint.phoneNumber,
        group: partner,
    };
}

function mapQueueEndpointToContact(endpoint: connect.Endpoint, partner: Partner): Contact {
    return {
        name: endpoint.name,
        number: endpoint.endpointARN,
        vdn: endpoint.endpointARN,
        group: partner,
        partner,
        isInternal: true,
    };
}
